<template>
  <div class="fixed bottom-6 right-6 z-50">
    <!-- Floating Chat Window -->
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 transform translate-y-4 scale-95"
      enter-to-class="opacity-100 transform translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 transform translate-y-0 scale-100"
      leave-to-class="opacity-0 transform translate-y-4 scale-95"
    >
      <div
        v-if="showChat"
        class="mb-4 h-96 w-[24.5rem] overflow-hidden rounded-lg shadow-2xl"
      >
        <ChatBot
          :is-floating="true"
          @close="showChat = false"
        />
      </div>
    </transition>

    <!-- Floating Button -->
    <button
      v-if="!showChat"
      @click="toggleChat"
      class="flex h-14 w-14 transform items-center justify-center rounded-full bg-primary-600 shadow-lg transition-all duration-300 hover:scale-110 hover:bg-primary-700"
      title="Abrir asistente de compras"
    >
      <transition
        enter-active-class="transition-all duration-200"
        enter-from-class="opacity-0 rotate-180"
        enter-to-class="opacity-100 rotate-0"
        leave-active-class="transition-all duration-200"
        leave-from-class="opacity-100 rotate-0"
        leave-to-class="opacity-0 rotate-180"
        mode="out-in"
      >
        <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 3.04.97 4.37L1 23l6.63-1.97C9.96 21.64 11.46 22 13 22h7c1.1 0 2-.9 2-2V12c0-5.52-4.48-10-10-10z"/>
        </svg>
      </transition>
    </button>

    <!-- Notification Badge -->
    <div
      v-if="hasUnreadMessages && !showChat"
      class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse"
    >
      {{ unreadCount }}
    </div>

    <!-- Welcome Tooltip -->
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 transform translate-x-4"
      enter-to-class="opacity-100 transform translate-x-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 transform translate-x-0"
      leave-to-class="opacity-0 transform translate-x-4"
    >
      <div
        v-if="showWelcomeTooltip && !showChat"
        class="absolute right-16 bottom-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 max-w-xs"
      >
        <div class="text-sm font-medium mb-1">¡Hola! 👋</div>
        <div class="text-xs text-gray-600 dark:text-gray-400">
          ¿Necesitas ayuda para encontrar productos? ¡Estoy aquí para ayudarte!
        </div>
        <div class="absolute right-0 top-1/2 transform translate-x-2 -translate-y-1/2">
          <div class="w-0 h-0 border-l-8 border-l-white dark:border-l-gray-800 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, defineAsyncComponent } from 'vue';

const ChatBot = defineAsyncComponent(() => import('./ChatBot.vue'));

const showChat = ref(false);
const hasUnreadMessages = ref(false);
const unreadCount = ref(0);
const showWelcomeTooltip = ref(false);

let welcomeTimeout: ReturnType<typeof setTimeout> | undefined;
let hideTooltipTimeout: ReturnType<typeof setTimeout> | undefined;

const toggleChat = () => {
  showChat.value = !showChat.value;
  
  if (showChat.value) {
    hasUnreadMessages.value = false;
    unreadCount.value = 0;
    showWelcomeTooltip.value = false;
  }

  // Track chat interaction
  if (window.trackCustomEvent) {
    window.trackCustomEvent('Chatbot', showChat.value ? 'Opened' : 'Closed', {
      source: 'floating_button'
    });
  }
};

const showWelcomeMessage = () => {
  // Show welcome tooltip after 3 seconds if chat hasn't been opened
  welcomeTimeout = setTimeout(() => {
    if (!showChat.value) {
      showWelcomeTooltip.value = true;
      
      // Hide tooltip after 5 seconds
      hideTooltipTimeout = setTimeout(() => {
        showWelcomeTooltip.value = false;
      }, 5000);
    }
  }, 3000);
};

onMounted(() => {
  showWelcomeMessage();
});

onUnmounted(() => {
  if (welcomeTimeout) clearTimeout(welcomeTimeout);
  if (hideTooltipTimeout) clearTimeout(hideTooltipTimeout);
});
</script>
